import { useCallback } from "react";
import { MutableRefObject } from "react";
import i18n from "@i18n/i18n";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { ModelTreeNode } from "@/types/ModelTreeNode";

/**
 * parseMultiplier
 * - unchanged: parses %, expressions, decimals
 */
export const parseMultiplier = (input: string): number | null => {
  if (!input) return null;
  const trimmed = input.trim();

  // 1. Check if it's a percentage like "15%"
  if (/^\d+(\.\d+)?%$/.test(trimmed)) {
    const percent = parseFloat(trimmed.replace("%", ""));
    return isNaN(percent) ? null : percent / 100;
  }

  // 2. Allow arithmetic expressions (numbers with +, -, *, /, ., and spaces)
  if (/^[0-9+\-*/().\s]+$/.test(trimmed)) {
    try {
      // eslint-disable-next-line no-new-func
      const result = new Function(`return (${trimmed})`)();
      if (typeof result === "number" && !isNaN(result) && isFinite(result)) {
        return result;
      }
      return null;
    } catch {
      return null;
    }
  }


  // 3. Check for numeric input with up to 2 decimal places
  if (/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    const value = parseFloat(trimmed);
    return isNaN(value) ? null : value;
  }

  return null;
};

const useMultiplier = () => {
  const updateFieldInChildren = (
    apiRef: MutableRefObject<GridApiPremium>,
    parentRow: any,
    newValue: string,
    field: string,
  ) => {
    if (!apiRef?.current) return;
    const parentPath = parentRow.path ?? [];
    const updates: any[] = [];

    apiRef.current.getRowModels().forEach((row: any) => {
      if (
        row.path.length > parentPath.length &&
        JSON.stringify(row.path.slice(0, parentPath.length)) ===
          JSON.stringify(parentPath)
      ) {
        updates.push({
          ...row,
          data: {
            ...row.data,
            [field]: newValue,
          },
        });
      }
    });

    if (updates.length > 0) {
      apiRef.current.updateRows(updates);
    }
  };

  const updateSystemInChildren = (
    apiRef: MutableRefObject<GridApiPremium>,
    parentRow: any,
    newValue: string,
  ) => updateFieldInChildren(apiRef, parentRow, newValue, "systemId");

  const updateSubSystemInChildren = (
    apiRef: MutableRefObject<GridApiPremium>,
    parentRow: any,
    newValue: string,
  ) => updateFieldInChildren(apiRef, parentRow, newValue, "subSystemId");

  const updateAttributeInChildren = (
    apiRef: MutableRefObject<GridApiPremium>,
    parentRow: any,
    newValue: string,
    attr: "attribute1" | "attribute2" | "attribute3",
  ) => updateFieldInChildren(apiRef, parentRow, newValue, attr);

  const getAncestorRows = (
    apiRef: MutableRefObject<GridApiPremium>,
    row: any,
  ) => {
    if (!row?.path) return [];
    const allRows = apiRef.current.getRowModels();
    const ancestors: any[] = [];

    for (let depth = 1; depth < row.path.length; depth++) {
      const ancestorPath = row.path.slice(0, depth);
      const ancestor = Array.from(allRows.values()).find(
        (r: any) => JSON.stringify(r.path) === JSON.stringify(ancestorPath),
      );
      if (ancestor) ancestors.push(ancestor);
    }
    return ancestors;
  };

  const updateNotesInChildren = (
    apiRef: MutableRefObject<GridApiPremium>,
    parentRow: any,
    newNotes: string,
  ) => {
    if (!apiRef?.current) return;

    const allRows = apiRef.current.getRowModels();
    const parentPath = parentRow.path ?? [];

    const rowsToUpdate: any[] = [];

    allRows.forEach((row: any) => {
      if (!row?.path) return;

      if (
        row.path.length > parentPath.length &&
        JSON.stringify(row.path.slice(0, parentPath.length)) ===
          JSON.stringify(parentPath)
      ) {
        rowsToUpdate.push({
          ...row,
          data: {
            ...row.data,
            notes: newNotes,
          },
          notes: newNotes,
        });
      }
    });

    if (rowsToUpdate.length > 0) {
      apiRef.current.updateRows(rowsToUpdate);
    }
  };

  /**
   * updateMultiplierInChildren
   * NOTES:
   * - This updates only multiplier metadata fields in the node and descendants:
   *   multiplierInput, multiplierValue, notes (if propagated).
   */
  const updateMultiplierInChildren = useCallback(
    (
      apiRef: MutableRefObject<GridApiPremium>,
      parentRow: any,
      newMultiplier: string | number,
    ) => {
      const parentKey = parentRow?.key;
      if (!parentKey) return;

      const parsedMultiplier = parentRow?.data?.multiplierValue ?? 1;
      const rawInput = parentRow?.data?.multiplierInput ?? (newMultiplier ?? "");

      if (rawInput.trim() !== "" && parentRow?.data?.multiplierValue === null) {
        apiRef.current.updateRows([
          {
            ...parentRow,
            data: {
              ...parentRow.data,
              error: i18n.t("scope-tab.multiplier.validation-message"),
            },
          },
        ]);
        return;
      }

      const allRows = apiRef.current
        .getSortedRowIds()
        .map((id) => apiRef.current.getRow(id));

      const descendants = allRows.filter((row) => {
        return (
          row?.path?.length > parentRow.path.length &&
          row?.path?.slice(0, parentRow.path.length).join("/") ===
            parentRow.path.join("/")
        );
      });

      // Update multiplier metadata only (no metric arithmetic)
      const rowsToUpdate: any[] = [];

      const updateMultiplierOnly = (node: any, inheritedMultiplier: number) => {
        const existingNotes = node?.data?.notes ?? "";

        const children = descendants.filter(
          (child) =>
            child?.path?.length === node?.path?.length + 1 &&
            child?.path?.slice(0, node?.path?.length).join("/") ===
              node?.path?.join("/"),
        );

        if (children.length > 0) {
          for (const child of children) {
            updateMultiplierOnly(child, inheritedMultiplier);
          }
        }

        const updatedNode = {
          ...node,
          multiplierInput: rawInput,
          multiplierValue: inheritedMultiplier,
          notes: existingNotes,
          data: {
            ...node.data,
            multiplierInput: rawInput,
            multiplierValue: inheritedMultiplier,
            notes: existingNotes,
          },
        };

        rowsToUpdate.push(updatedNode);
      };

      updateMultiplierOnly(parentRow, parsedMultiplier);

      if (rowsToUpdate.length > 0) {
        apiRef.current.updateRows(rowsToUpdate);
      }

      // compute and set hasChildMultiplierOverride for ancestors immediately
      const allRowsMap = Array.from(apiRef.current.getRowModels().values());

      const ancestors = getAncestorRows(apiRef, parentRow);

      const ancestorUpdates: any[] = [];

      ancestors.forEach((ancestor) => {
        const ancestorMultiplierRaw = ancestor?.data?.multiplierValue;
        if (ancestorMultiplierRaw === null || ancestorMultiplierRaw === undefined) {
          return;
        }
        const ancestorMultiplier = Number(ancestorMultiplierRaw);

        const descendantDiffers = allRowsMap.some((r: any) => {
          if (!r?.path || r.path.length <= ancestor.path.length) return false;
          if (JSON.stringify(r.path.slice(0, ancestor.path.length)) !== JSON.stringify(ancestor.path)) return false;
          const childValRaw = r?.data?.multiplierValue;
          const hasChildVal =
            childValRaw !== null &&
            childValRaw !== undefined &&
            !isNaN(Number(childValRaw));
          if (!hasChildVal) return false;
          return Number(childValRaw) !== ancestorMultiplier;
        });

        if (ancestor.data?.hasChildMultiplierOverride !== descendantDiffers) {
          ancestorUpdates.push({
            ...ancestor,
            data: {
              ...ancestor.data,
              hasChildMultiplierOverride: descendantDiffers,
            },
            hasChildMultiplierOverride: descendantDiffers,
          });
        }
      });

      if (ancestorUpdates.length > 0) {
        apiRef.current.updateRows(ancestorUpdates);
      }
    },
    [],
  );

  /**
   * applyMultiplierTotals:
   * - IMPORTANT: purposely **does NOT** multiply original totals.
   * - It ensures displayed totals use the original values if present
   *   and doesn't attempt to apply multiplier arithmetic.
   */
  const applyMultiplierTotals = (nodes: ModelTreeNode[]): ModelTreeNode[] => {
    return nodes.map((node) => {
      return {
        ...node,
        data: {
          ...node.data,
        },
      };
    });
  };

  const updateCarbonImpactInChildren = useCallback(
    (
      apiRef: MutableRefObject<GridApiPremium>,
      parentRow: any,
      newMultiplier: string | number,
    ) => {
      const parentKey = parentRow?.key;
      if (!parentKey) return;

      const parsedMultiplier = parentRow?.data?.carbonImpactValue ?? 1;
      const rawInput = parentRow?.data?.carbonImpactInput ?? newMultiplier ?? "";

      if (rawInput.trim() !== "" && parentRow?.data?.carbonImpactValue === null) {
        apiRef.current.updateRows([
          {
            ...parentRow,
            data: {
              ...parentRow.data,
              carbonError: i18n.t("scope-tab.multiplier.validation-message"),
            },
          },
        ]);
        return;
      }

      const allRows = apiRef.current
        .getSortedRowIds()
        .map((id) => apiRef.current.getRow(id));

      const descendants = allRows.filter((row) => {
        return (
          row?.path?.length > parentRow.path.length &&
          row?.path?.slice(0, parentRow.path.length).join("/") ===
            parentRow.path.join("/")
        );
      });

      // Recursive aggregation update for carbonImpact metadata only
      const rowsToUpdate: any[] = [];

      const updateAndAggregate = (
        node: any,
        inheritedMultiplier: number,
      ) => {
        const existingNotes = node?.data?.carbonImpactNotes ?? "";

        const children = descendants.filter(
          (child) =>
            child?.path?.length === node?.path?.length + 1 &&
            child?.path?.slice(0, node?.path?.length).join("/") ===
              node?.path?.join("/"),
        );

        if (children.length > 0) {
          for (const child of children) {
            updateAndAggregate(child, inheritedMultiplier);
          }
        }

        const updatedNode = {
          ...node,
          carbonImpactInput: rawInput,
          carbonImpactValue: inheritedMultiplier,
          carbonImpactNotes: existingNotes,
          data: {
            ...node.data,
            carbonImpactInput: rawInput,
            carbonImpactValue: inheritedMultiplier,
            carbonImpactNotes: existingNotes,
          },
        };

        rowsToUpdate.push(updatedNode);
      };

      updateAndAggregate(parentRow, parsedMultiplier);

      if (rowsToUpdate.length > 0) {
        apiRef.current.updateRows(rowsToUpdate);
      }

      // compute and set hasChildCarbonImpactOverride for ancestors immediately
      const allRowsMap = Array.from(apiRef.current.getRowModels().values());

      const ancestors = getAncestorRows(apiRef, parentRow);

      const ancestorUpdates: any[] = [];

      ancestors.forEach((ancestor) => {
        // require ancestor multiplier numeric to compare
        const ancestorMultiplierRaw = ancestor?.data?.carbonImpactValue;
        if (ancestorMultiplierRaw === null || ancestorMultiplierRaw === undefined) {
          // if ancestor has no multiplier value, skip marking (we only flag when ancestor has numeric multiplier)
          return;
        }
        const ancestorMultiplier = Number(ancestorMultiplierRaw);

        // find any descendant under this ancestor that has a numeric multiplier differing from ancestor
        const descendantDiffers = allRowsMap.some((r: any) => {
          if (!r?.path || r.path.length <= ancestor.path.length) return false;
          if (JSON.stringify(r.path.slice(0, ancestor.path.length)) !== JSON.stringify(ancestor.path)) return false;
          const childValRaw = r?.data?.carbonImpactValue;
          const hasChildVal =
            childValRaw !== null &&
            childValRaw !== undefined &&
            !isNaN(Number(childValRaw));
          if (!hasChildVal) return false;
          return Number(childValRaw) !== ancestorMultiplier;
        });

        if (ancestor.data?.hasChildCarbonImpactOverride !== descendantDiffers) {
          ancestorUpdates.push({
            ...ancestor,
            data: {
              ...ancestor.data,
              hasChildCarbonImpactOverride: descendantDiffers,
            },
            hasChildCarbonImpactOverride: descendantDiffers,
          });
        }
      });

      if (ancestorUpdates.length > 0) {
        apiRef.current.updateRows(ancestorUpdates);
      }
    },
    [],
  );

  const updateCarbonImpactNotesInChildren = (
    apiRef: MutableRefObject<GridApiPremium>,
    parentRow: any,
    newNotes: string,
  ) => {
    if (!apiRef?.current) return;

    const allRows = apiRef.current.getRowModels();
    const parentPath = parentRow.path ?? [];

    const rowsToUpdate: any[] = [];

    allRows.forEach((row: any) => {
      if (!row?.path) return;

      if (
        row.path.length > parentPath.length &&
        JSON.stringify(row.path.slice(0, parentPath.length)) ===
          JSON.stringify(parentPath)
      ) {
        rowsToUpdate.push({
          ...row,
          data: {
            ...row.data,
            carbonImpactNotes: newNotes,
          },
          carbonImpactNotes: newNotes,
        });
      }
    });

    if (rowsToUpdate.length > 0) {
      apiRef.current.updateRows(rowsToUpdate);
    }
  };

  return {
    parseMultiplier,
    updateNotesInChildren,
    updateMultiplierInChildren,
    applyMultiplierTotals,
    getAncestorRows,
    updateSystemInChildren,
    updateSubSystemInChildren,
    updateAttributeInChildren,
    updateCarbonImpactInChildren,
    updateCarbonImpactNotesInChildren
  };
};

export default useMultiplier;
